'use client'

import { motion } from 'framer-motion'
import { ArrowUpRightIcon, LockIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CryptoIcon } from '@/components/widget/crypto-icon'
import { HOME_SPRING, HomeHeaderLink, HomePanel } from '@/features/home/components/home-motion'
import type { HomeDailyBrief } from '@/features/home/hooks/use-home-daily-brief'
import { SCENARIO_RANGE, useHomeScenario } from '@/features/home/hooks/use-home-scenario'
import { pnlAtMove, type ScenarioGrid, timeMatters } from '@/features/home/lib/home-scenario'
import { useNumberFormat } from '@/hooks/use-number-format'
import { cn } from '@/lib/utils'

const MAX_COINS = 4
const CHART_W = 400
const CHART_H = 150
const PAD_Y = 10

/** PnL change across the price moves: today solid, four weeks out dashed when options decay. */
function ScenarioChart({ grid, showLater }: { grid: ScenarioGrid; showLater: boolean }) {
  const fmt = useNumberFormat()
  const t = useTranslations('trade.home.scenario')
  const rows = showLater ? [0, grid.pnl.length - 1] : [0]
  const values = rows.flatMap((row) => grid.pnl[row])
  const top = Math.max(0, ...values)
  const bottom = Math.min(0, ...values)
  const span = top - bottom || 1
  const x = (index: number) => (index / (grid.moves.length - 1)) * CHART_W
  const y = (value: number) => PAD_Y + ((top - value) / span) * (CHART_H - PAD_Y * 2)
  const path = (row: number) =>
    grid.pnl[row].map((value, index) => `${index ? 'L' : 'M'}${x(index)},${y(value)}`).join(' ')
  const zeroIndex = grid.moves.findIndex((move) => Math.abs(move) < 1e-9)

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        preserveAspectRatio="none"
        className="h-36 w-full overflow-visible"
        role="img"
        aria-label={t('chartLabel')}
      >
        <line
          x1={0}
          x2={CHART_W}
          y1={y(0)}
          y2={y(0)}
          className="stroke-foreground/15"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
        {zeroIndex >= 0 && (
          <line
            x1={x(zeroIndex)}
            x2={x(zeroIndex)}
            y1={0}
            y2={CHART_H}
            className="stroke-foreground/15"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {showLater && (
          <motion.path
            d={path(grid.pnl.length - 1)}
            fill="none"
            className="stroke-shell-accent"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.15 }}
          />
        )}
        <motion.path
          d={path(0)}
          fill="none"
          className="stroke-foreground"
          strokeWidth={1.75}
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        />
      </svg>
      <span className="text-foreground/40 absolute top-0 left-0 text-[10px]">
        +{fmt.usd(top, { sensitive: true })}
      </span>
      <span className="text-foreground/40 absolute bottom-0 left-0 text-[10px]">
        {bottom < 0 ? '−' : ''}
        {fmt.usd(Math.abs(bottom), { sensitive: true })}
      </span>
      <div className="text-foreground/40 mt-1 flex justify-between text-[10px]">
        <span>−{SCENARIO_RANGE}%</span>
        <span>{t('today')}</span>
        <span>+{SCENARIO_RANGE}%</span>
      </div>
      {showLater && (
        <div className="text-foreground/50 mt-1 flex gap-4 text-[10px]">
          <span className="flex items-center gap-1.5">
            <span className="bg-foreground h-0.5 w-3" />
            {t('legendNow')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="border-shell-accent w-3 border-t border-dashed" />
            {t('legendLater', { weeks: grid.times.length - 1 })}
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * "If the price moves" (Alex's ask, in place of the liquidation explainer): the Risk Slides
 * simulation of the user's open positions in one coin — what the book makes or loses if the
 * price moves up to 20% either way, today and over the next weeks (options lose time value) —
 * as a curve and a matrix, with the coin switcher and a way into the full Risk Slides page.
 */
export function HomeScenario({
  brief,
  onOpenRiskSlides,
  className,
}: {
  brief: HomeDailyBrief
  onOpenRiskSlides: () => void
  className?: string
}) {
  const t = useTranslations('trade.home.scenario')
  const fmt = useNumberFormat()
  const coins = brief.exposures
    .filter((exposure) => exposure.positions.length > 0)
    .slice(0, MAX_COINS)
    .map((exposure) => exposure.asset)
  const [picked, setPicked] = useState<string | null>(null)
  const asset = picked && coins.includes(picked) ? picked : (coins[0] ?? null)
  const { grid, allowed, hasPositions, isPending, isError } = useHomeScenario(asset)
  const later = grid ? timeMatters(grid) : false
  const columns = grid
    ? grid.moves
        .map((move, index) => ({ move, index }))
        .filter(({ move }) => Math.abs(Math.round(move / 5) * 5 - move) < 1e-6)
    : []
  const rows = grid ? (later ? grid.times.map((_, index) => index) : [0]) : []
  const peak = grid ? Math.max(1, ...grid.pnl.flat().map(Math.abs)) : 1
  const compact = (value: number) =>
    fmt.number(value, {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
      signDisplay: 'exceptZero',
      sensitive: true,
    })

  return (
    <HomePanel
      title={t('title')}
      className={className}
      aside={
        <>
          {coins.length > 1 && (
            <div className="flex flex-wrap justify-end gap-1">
              {coins.map((coin) => (
                <button
                  key={coin}
                  type="button"
                  onClick={() => setPicked(coin)}
                  className={cn(
                    'relative flex cursor-pointer items-center gap-1 rounded-full px-2 py-0.5 text-[11px] transition-colors',
                    coin === asset
                      ? 'text-emphasis-foreground'
                      : 'text-foreground/55 hover:text-foreground'
                  )}
                >
                  {coin === asset && (
                    <motion.span
                      layoutId="home-scenario-coin"
                      transition={HOME_SPRING}
                      className="bg-emphasis absolute inset-0 rounded-full"
                    />
                  )}
                  <CryptoIcon symbol={coin} className="relative size-3" />
                  <span className="relative">{coin}</span>
                </button>
              ))}
            </div>
          )}
          <HomeHeaderLink onClick={onOpenRiskSlides}>{t('openRiskSlides')}</HomeHeaderLink>
        </>
      }
    >
      {!allowed ? (
        <div className="flex flex-col items-start gap-3 py-6">
          <p className="text-foreground/60 flex items-center gap-2 text-sm">
            <LockIcon className="size-3.5" />
            {t('locked')}
          </p>
          <Button size="sm" variant="outline" onClick={onOpenRiskSlides}>
            {t('openRiskSlides')}
            <ArrowUpRightIcon className="size-3.5" />
          </Button>
        </div>
      ) : !asset || !hasPositions ? (
        <p className="text-foreground/50 py-6 text-sm">{t('empty')}</p>
      ) : isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : isError || !grid ? (
        <p className="text-foreground/50 py-6 text-sm">{t('error')}</p>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-foreground text-sm leading-6">
            {t('summary', {
              asset,
              down: compact(pnlAtMove(grid, 0, -SCENARIO_RANGE / 2)),
              up: compact(pnlAtMove(grid, 0, SCENARIO_RANGE / 2)),
            })}
          </p>
          <ScenarioChart grid={grid} showLater={later} />
          {/* The matrix: rows are dates, columns 5% moves; tinted by size and sign. */}
          <div className="-mx-1 overflow-x-auto">
            <table className="w-full min-w-[30rem] border-separate border-spacing-1 text-[11px]">
              <thead>
                <tr>
                  <th className="text-foreground/40 w-16 text-left font-normal" />
                  {columns.map(({ move }) => (
                    <th
                      key={move}
                      className={cn(
                        'text-right font-normal',
                        Math.abs(move) < 1e-9 ? 'text-foreground' : 'text-foreground/40'
                      )}
                    >
                      {move > 0 ? '+' : ''}
                      {fmt.number(move, { maximumFractionDigits: 1 })}%
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={grid.times[row]}>
                    <th className="text-foreground/50 text-left font-normal whitespace-nowrap">
                      {row === 0 ? t('today') : t('inWeeks', { count: row })}
                    </th>
                    {columns.map(({ move, index }) => {
                      const value = grid.pnl[row][index]
                      const strength = Math.min(1, Math.abs(value) / peak)
                      return (
                        <Tooltip key={move}>
                          <TooltipTrigger asChild>
                            <td
                              className={cn(
                                'cursor-default rounded-xs px-1.5 py-1 text-right',
                                value > 0
                                  ? strength > 0.5
                                    ? 'bg-chart-mint/25 text-chart-mint'
                                    : 'bg-chart-mint/10 text-chart-mint'
                                  : value < 0
                                    ? strength > 0.5
                                      ? 'bg-chart-red/25 text-chart-red'
                                      : 'bg-chart-red/10 text-chart-red'
                                    : 'bg-foreground/5 text-foreground/50'
                              )}
                            >
                              {compact(value)}
                            </td>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('cellTooltip', {
                              move: `${move > 0 ? '+' : ''}${fmt.number(move, { maximumFractionDigits: 1 })}%`,
                              when: row === 0 ? t('today') : t('inWeeks', { count: row }),
                              pnl: fmt.usd(value, { sensitive: true }),
                            })}
                          </TooltipContent>
                        </Tooltip>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-foreground/35 text-[11px] leading-4">{t('note', { asset })}</p>
        </div>
      )}
    </HomePanel>
  )
}
